import { ContentType } from 'types/publishedAssets';
import { DEFAULT_CROP_KEY } from 'types/imageCrop';
import { useGetContentTranslations } from 'utils/useGetContentTranslations';
import { Spacer } from 'components/Atoms/Spacer/Spacer';
import { ImageBoss } from 'components/Content/Atoms/ImageBoss';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { useContext } from 'react';
import { PageContext } from 'src/context/PageContext';
import { ActionButton, ButtonType } from 'components/Atoms/ActionButton/ActionButton';
import { Icon } from 'components/Atoms/Icon/Icon';
import { useTranslation } from 'react-i18next';
import { determineAdvertiseOnPage, findAdvertiseOnPageSlug } from 'utils/determineAdvertiseOnPage';
import { hexToRgba } from 'utils/hexToRgba';
import { readableTextColor } from 'utils/readableTextColor';
import { useIsRTL } from 'utils/useIsRTL';
import { useLocalizedDate } from 'utils/useLocalizedDate';
import { buildPreviewAwarePath } from 'utils/previewLink';
import { TabNavigation } from '../TabNavigation/TabNavigation';
import { HeroIllustration } from './HeroIllustration';
import { heroHeadingColor } from './heroHeadingColor';
import styles from './Hero.module.scss';

const HERO_DEFAULT_BACKGROUND_COLOR = '#f7f7f7';

interface HeroProps {
  content: ContentType;
}

// note add new line before and after to not interfear with grid
const applyLineBreaks = (source: string) => {
  let lineBreaks = source.replace(/(?:\r\n\r\n|\n\n)/g, '\n\n<br />\n\n'); // double line break
  lineBreaks = lineBreaks.replace(/(?:\r\n)(?!\s*\|)/g, '\n\n'); // single line break, but not for table rows
  return lineBreaks;
};

export function Hero(props: HeroProps) {
  const { t } = useTranslation();
  const { content } = props;
  const { translate, getMediaItem, getImageCrop } = useGetContentTranslations();
  const { page, urlParams, theme, linkHostname, slug } = useContext(PageContext);
  const { isRTL } = useIsRTL();
  const formattedReleaseDate = useLocalizedDate(page?.released_at || '', 'MMMM d, yyyy');
  const isUpdatePage = page?.page_type === 'update';
  // TODO: handle multiple advertise on pages
  const advertiseOnPage = isUpdatePage
    ? determineAdvertiseOnPage([urlParams?.projectId], page?.advertise_on_pages)
    : null;
  const advertiseOnPageTitle = translate(advertiseOnPage?.translations, 'title');
  const advertiseOnPageSlug = advertiseOnPage ? findAdvertiseOnPageSlug(advertiseOnPage) : undefined;
  const advertiseOnPageId = advertiseOnPage ? advertiseOnPage.id : undefined;
  const advertiseOnPageType = advertiseOnPage?.page_type === 'project' ? 'projects' : 'pages';
  const backToProjectHref = buildPreviewAwarePath(
    [
      `${urlParams?.organization}`,
      `${urlParams?.locale}`,
      advertiseOnPageType,
      `${advertiseOnPageSlug || advertiseOnPageId}`,
    ],
    'page',
    { urlParams, slug, linkHostname },
  );
  const heading = translate(content.translations, 'heading');
  const description = translate(content.translations, 'description');
  const mediaItem = getMediaItem(content, 0);
  const imageCrop = getImageCrop(content, 0);
  const hasUploadedImage = !!mediaItem?.url;
  // Without an uploaded image, the hero always shows a background color (custom or
  // the neutral default) plus a decorative illustration whose color contrasts with it.
  const effectiveBackgroundColor = hasUploadedImage
    ? undefined
    : content?.metadata?.background_color || HERO_DEFAULT_BACKGROUND_COLOR;
  const illustrationVariant =
    effectiveBackgroundColor && readableTextColor(effectiveBackgroundColor) === '#fff' ? 'light' : 'dark';
  const heroImageAlt = mediaItem?.alt_text || heading || '';
  let textPosition = content?.metadata?.text_position || 'left';
  if (isRTL && textPosition === 'left') {
    textPosition = 'right';
  } else if (isRTL && textPosition === 'right') {
    textPosition = 'left';
  }

  const textBlockOpacity = content?.metadata?.text_block_opacity ?? 90;
  // A stored crop already frames the image, so the focal-point CSS is not applied on
  // top of it (that would double-shift). image_focal_point stays the no-crop fallback.
  const hasImageCrop = !!imageCrop?.crops?.[DEFAULT_CROP_KEY];
  const imageFocalPoint = hasImageCrop ? 'center' : content?.metadata?.image_focal_point || 'center';
  const textBlockAlpha = textBlockOpacity / 100;
  const metadataTextBlockColor = content?.metadata?.text_block_background_color ?? '';
  const hasCustomTextBlockColor = !!hexToRgba(metadataTextBlockColor, 1);
  // The text color decision uses the same resolved color as the block background.
  const textBlockColor = hasCustomTextBlockColor ? metadataTextBlockColor : '#f7f7f7';
  const textBlockBackground = hexToRgba(textBlockColor, textBlockAlpha) ?? `rgba(247, 247, 247, ${textBlockAlpha})`;
  const readableColor = readableTextColor(textBlockColor);
  const textBlockStyle = {
    backgroundColor: textBlockBackground,
    color: readableColor,
    float: textPosition === 'left' ? ('left' as const) : ('right' as const),
  };
  // globals.css colors every h1-h6 with the brand headline token, which outranks the
  // color the text block passes down, so the heading needs its own checked color.
  const headingStyle = {
    color: heroHeadingColor({ textBlockColor, hasCustomTextBlockColor, headlineColor: theme?.whitelabelTextColor }),
  };
  const markdownText = applyLineBreaks(description);
  const showDynamicContent = isUpdatePage && !content?.metadata?.hide_dynamic_content;
  return (
    <>
      <div
        className={styles.heroSection}
        style={effectiveBackgroundColor ? { backgroundColor: effectiveBackgroundColor } : undefined}
      >
        {hasUploadedImage && (
          <div className={styles.heroImage} style={{ ['--object-position' as string]: imageFocalPoint }}>
            <ImageBoss
              url={mediaItem.url}
              alt={heroImageAlt}
              width={1920}
              height={400}
              className={styles.heroImg}
              imageCrop={imageCrop}
            />
          </div>
        )}
        {!hasUploadedImage && (
          <HeroIllustration variant={illustrationVariant} className={styles.heroIllustration} isRTL={isRTL} />
        )}
        <div className={styles.heroSectionContent} style={textBlockStyle}>
          {isUpdatePage && (
            <>
              <ActionButton type={ButtonType.HOME} href={backToProjectHref}>
                <Icon name="arrow-left" /> {t('project')}
              </ActionButton>
              <Spacer orientation="vertical" customSize={16} />
            </>
          )}
          <h1 style={headingStyle}>{heading}</h1>
          <Spacer orientation="vertical" customSize={8} />
          <div className={styles.description}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} linkTarget="_blank">
              {markdownText}
            </ReactMarkdown>
          </div>
          {showDynamicContent && (
            <>
              <Spacer orientation="vertical" customSize={8} />
              <p>{formattedReleaseDate}</p>
              <p>{advertiseOnPageTitle}</p>
            </>
          )}
        </div>
      </div>
      <TabNavigation />
      <Spacer orientation="vertical" customSize={24} />
    </>
  );
}
